import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { Spinner } from './components/Spinner';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { PendingApproval } from './pages/PendingApproval';
import { LesseeDashboard } from './pages/lessee/LesseeDashboard';
import { Onboarding } from './pages/lessee/Onboarding';
import { Profile } from './pages/lessee/Profile';
import { Documents } from './pages/lessee/Documents';
import { BookFlight } from './pages/lessee/BookFlight';
import { Logbook } from './pages/Logbook';
import { MyBookings } from './pages/lessee/MyBookings';
import { MyFlights } from './pages/lessee/MyFlights';
import { LogFlight } from './pages/lessee/LogFlight';
import { Preflight } from './pages/lessee/Preflight';
import { Squawks } from './pages/lessee/Squawks';
import { ReportSquawk } from './pages/lessee/ReportSquawk';
import { Billing } from './pages/lessee/Billing';
import { Statement } from './pages/lessee/Statement';
import { Responsibilities } from './pages/lessee/Responsibilities';
import { CheckoutSignoff } from './pages/cfi/CheckoutSignoff';
import { StudentFlightApproval } from './pages/cfi/StudentFlightApproval';
import { Resources } from './pages/lessee/Resources';
import { FlightTracker } from './pages/FlightTracker';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminLessees } from './pages/admin/AdminLessees';
import { AdminLesseeDetail } from './pages/admin/AdminLesseeDetail';
import { AdminVerifyDocs } from './pages/admin/AdminVerifyDocs';
import { AdminBookings } from './pages/admin/AdminBookings';
import { AdminFlights } from './pages/admin/AdminFlights';
import { AdminUtilization } from './pages/admin/AdminUtilization';
import { AdminSquawks } from './pages/admin/AdminSquawks';
import { AdminAircraft } from './pages/admin/AdminAircraft';
import { AdminAgreements } from './pages/admin/AdminAgreements';
import { AdminRevenue } from './pages/admin/AdminRevenue';
import { AdminSettings } from './pages/admin/AdminSettings';
import { PrivacyPolicy, Terms } from './pages/Legal';
import { LogoGallery } from './pages/LogoGallery';
import { InstructorIntake } from './pages/InstructorIntake';
import { BadgeApplication } from './pages/BadgeApplication';
import { PaymentPreview } from './pages/PaymentPreview';
import { BillingPreview } from './pages/BillingPreview';

export default function App() {
  const { ready, fbUser, profile, isAdmin, configured } = useAuth();
  const loc = useLocation();

  // Public, no-auth pages. Netlify 301s /instructor to /instructor/ (the OG
  // share-card page is a real directory), so match with or without the slash.
  const publicPath = loc.pathname.replace(/\/+$/, '') || '/';
  if (publicPath === '/instructor') return <InstructorIntake />;
  if (publicPath === '/badge' || (publicPath === '/' && window.location.hostname.startsWith('badge.'))) return <BadgeApplication />;
  if (publicPath === '/privacy') return <PrivacyPolicy />;
  if (publicPath === '/terms') return <Terms />;
  if (publicPath === '/logos') return <LogoGallery />;
  if (publicPath === '/billing-preview') return <BillingPreview />;

  if (!ready) return <Spinner label="Loading portal" />;

  // Card-on-file preview (needs the signed-in user for the Stripe SetupIntent).
  if (loc.pathname === '/payment-preview') return <PaymentPreview />;

  if (!fbUser) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: loc }} />} />
      </Routes>
    );
  }

  if (!profile) {
    return <PendingApproval />;
  }

  if (profile.status === 'pending_approval') return <PendingApproval />;
  // Onboarding is done but the owner hasn't approved yet — no booking until approved.
  if (profile.status === 'verification_pending') return <PendingApproval />;
  if (profile.status === 'terminated') {
    return (
      <Layout>
        <div className="card p-8 text-center text-ink-muted">
          Your account has been terminated. Contact AJM Aviation if this is in error.
        </div>
      </Layout>
    );
  }
  if (profile.status === 'suspended') {
    return (
      <Layout>
        <div className="card p-8 text-center text-ink-muted">
          Your account is temporarily suspended. Please contact AJM Aviation to restore access.
        </div>
      </Layout>
    );
  }

  if (isAdmin) {
    return (
      <Layout>
        {!configured && <ConfigWarning />}
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/lessees" element={<AdminLessees />} />
          <Route path="/admin/lessees/:uid" element={<AdminLesseeDetail />} />
          <Route path="/admin/verify" element={<AdminVerifyDocs />} />
          <Route path="/admin/bookings" element={<AdminBookings />} />
          <Route path="/admin/book" element={<BookFlight />} />
          <Route path="/admin/logbook" element={<Logbook />} />
          <Route path="/admin/flights" element={<AdminFlights />} />
          <Route path="/admin/utilization" element={<AdminUtilization />} />
          <Route path="/admin/checkout" element={<CheckoutSignoff />} />
          <Route path="/admin/log-flight" element={<LogFlight />} />
          <Route path="/admin/tracker" element={<FlightTracker />} />
          <Route path="/admin/squawks" element={<AdminSquawks />} />
          {/* Owner files into the same shared log as the pilots — same form. */}
          <Route path="/admin/squawks/new" element={<ReportSquawk />} />
          <Route path="/admin/aircraft" element={<AdminAircraft />} />
          {/* Same page the renters see — the owner needs to read what they read. */}
          <Route path="/admin/resources" element={<Resources />} />
          <Route path="/admin/agreements" element={<AdminAgreements />} />
          <Route path="/admin/revenue" element={<AdminRevenue />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </Layout>
    );
  }

  // New lessees complete the guided onboarding wizard before anything else.
  // It renders full-screen (its own header/progress), outside the app Layout.
  if (profile.status === 'profile_incomplete') {
    return <Onboarding />;
  }

  return (
    <Layout>
      {!configured && <ConfigWarning />}
      <Routes>
        <Route path="/" element={<LesseeDashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/book" element={<BookFlight />} />
        <Route path="/logbook" element={<Logbook />} />
        <Route path="/bookings" element={<MyBookings />} />
        <Route path="/flights" element={<MyFlights />} />
        <Route path="/flights/new" element={<LogFlight />} />
        <Route path="/preflight" element={<Preflight />} />
        <Route path="/squawks" element={<Squawks />} />
        <Route path="/squawks/new" element={<ReportSquawk />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing/:cycleId" element={<Statement />} />
        <Route path="/responsibilities" element={<Responsibilities />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/checkout" element={<CheckoutSignoff />} />
        <Route path="/approve-flights" element={<StudentFlightApproval />} />
        <Route path="/tracker" element={<FlightTracker />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function ConfigWarning() {
  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <strong className="font-semibold">Setup pending.</strong> Firebase env vars aren’t set. See{' '}
      <code>SETUP.md</code>.
    </div>
  );
}
